import { ThemeProvider } from '@mui/material';
import { MemreButton, MemreFlexBox, MemrePageContainer, MemreText } from 'components/core';
import * as Color from 'core/colors';
import theme from 'pages/learner-dashboard/visulisations/theme';
import React from 'react';

type Props = {
  setIsModalOpen: (isModalOpen: boolean) => void;
};

const roleDetails = {
  admin: {
    title: '🛠 Admin',
    description:
      '• Full access to all courses, content, learners, and departments\n• Create, edit, publish, archive, or delete any course\n• Create and manage all content (study items, sets, series)\n• Add and remove learners and assignments across all courses\n• Assign or change staff roles for users\n• View and export reports across the entire partner and its sub-accounts (departments)',
  },
  course_manager: {
    title: '📘 Course Manager',
    description:
      '• Create and manage any course\n• Add and remove learners and assignments\n• Set study goals and schedules\n• Publish or archive courses\n• View learner progress and analytics\n• Cannot assign staff roles, manage user accounts, or edit organization settings',
  },
  content_manager: {
    title: '✏️ Content Manager',
    description:
      '• Create and edit study items, sets, and series\n• Organize content in the shared content library\n• Copy or share content between courses\n• Cannot assign learners or manage any course settings\n• Cannot access learner data or analytics',
  },
  course_viewer: {
    title: '📊 Course Viewer',
    description:
      '• View all course dashboards and learner performance\n• Filter analytics by course, department, or user\n• Export available reports\n• Cannot edit content or courses\n• Cannot manage learners or staff',
  },
  instructor: {
    title: '👩‍🏫 Instructor (Course-Level Only)',
    description:
      "• Assigned on a per-course basis\n• Full control over the courses they're assigned to:\n  •• Add and manage learners\n  •• Create new content or copy from shared assignments\n  •• Edit course descriptions, goals, and schedules\n  •• Publish, archive, or delete the course\n  •• View learner progress and course analytics\n• Can import from shared assignments, but must make a copy to edit\n• Cannot access other courses unless explicitly assigned\n• Instructors are visible to learners within the course",
  },
};

export const RoleInfoModal = (props: Props) => {
  const { setIsModalOpen } = props;

  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          height: '100%',
          width: '100%',
          zIndex: '100',
          position: 'absolute',
          top: '0',
          left: '0',
          backgroundColor: 'white',
        }}
      >
        <MemrePageContainer>
          <MemreFlexBox gap={4} direction="column" sx={{ padding: '1rem' }}>
            <MemreFlexBox gap={4} direction="row" justify="space-between" align="center">
              <MemreText variant="h3">Role Information</MemreText>
              <MemreButton
                text="Close"
                variant="outlined"
                onClick={() => setIsModalOpen(false)}
                color="error"
                iconName="close"
                size="small"
              />
            </MemreFlexBox>
            <MemreFlexBox gap={3} direction="column">
              {Object.entries(roleDetails).map(([roleKey, role]) => (
                <MemreFlexBox
                  key={roleKey}
                  gap={1}
                  direction="column"
                  sx={{
                    padding: '1rem',
                    border: `1px solid ${Color.lightGray}`,
                    borderRadius: '8px',
                    backgroundColor: Color.veryLightGray,
                  }}
                >
                  <MemreText variant="h5" sx={{ fontWeight: 'bold' }}>
                    {role.title}
                  </MemreText>
                  <MemreText variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {role.description}
                  </MemreText>
                </MemreFlexBox>
              ))}
              <MemreFlexBox justify="center" sx={{ marginTop: '1rem' }}>
                <MemreText variant="body2">
                  For more information, visit our{' '}
                  <a
                    href="https://www.cerego.com/support/staff"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: Color.primaryBlue, textDecoration: 'underline' }}
                  >
                    staff documentation
                  </a>
                </MemreText>
              </MemreFlexBox>
            </MemreFlexBox>
          </MemreFlexBox>
        </MemrePageContainer>
      </div>
    </ThemeProvider>
  );
};
